/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import React, { useState, useEffect, useMemo } from 'react';
import {
  makeStyles, Box, Container, Grid, Divider, Typography, useTheme, useMediaQuery,
} from '@material-ui/core';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import VehicleDescription from 'views/vehicle_page/components/VehicleDescription';
import HostedBy from 'common/components/HostedBy';
import TechnicalDetails from 'common/components/TechnicalDetails';
import MostRidersAskAbout from 'views/vehicle_page/components/MostRidersAskAbout';
import Reviews from 'views/vehicle_page/components/Reviews';
import StickyBar from 'views/vehicle_page/components/StickyBar';
import getVehicleDetails from 'views/vehicle_page/utils/getVehicleDetails';
import getOwnerDetails from 'views/vehicle_page/utils/getOwnerDetails';
import HeroGallery from 'common/components/HeroGallery';
import useHandleResponse from 'utils/useHandleResponse';
import formatMilesLimit from 'views/vehicle_page/utils/formatMilesLimit';
import axios, { API } from 'config/axios';
import { Alert, AlertTitle } from '@material-ui/lab';

import {
  useLayoutSnackbar,
  useLayoutState,
  useLayoutLoadingSpinner,
} from 'components/shared/Layout';
import { Router, withTranslation } from 'config/i18n';
import dayjs from 'dayjs';
import VehiclePageQuote from 'views/vehicle_page/components/VehiclePageQuote';

const SummaryDialog = dynamic(() => import('views/vehicle_page/components/SummaryDialog'));

const AuthOverlay = dynamic(() => import('common/components/auth/AuthOverlay'));

const useStyles = makeStyles((theme) => ({
  carouselWrapper: {
    marginTop: theme.spacing(-7.75),
    position: 'relative',
    zIndex: '4',
  },
  contentWrapper: {
    paddingTop: theme.spacing(3.5),
    paddingBottom: theme.spacing(6),

    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(0.5),
    },
  },
  sectionTitle: {
    fontWeight: '500',
    marginBottom: theme.spacing(1),
    position: 'relative',
  },
  sectionDivider: {
    margin: theme.spacing(4, 0),

    [theme.breakpoints.up('lg')]: {
      margin: theme.spacing(6, 0),
    },
  },
  dividerDescription: {
    margin: theme.spacing(2, 0),

    [theme.breakpoints.up('lg')]: {
      margin: theme.spacing(3, 0),
    },
  },
  technicalDetailsTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  technicalDetailsHint: {
    color: theme.palette.secondary.light,
  },
  sympol: {
    position: 'absolute',
    top: theme.spacing(-0.5),
    right: theme.spacing(-2),
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.secondary.light,
    fontWeight: '400',
  },

  bookingDisabledBannerAlert: {
    backgroundColor: 'rgba(243, 224, 94, 0.15)',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
  },
  bannerTitle: {
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  bannerMessage: {
  },
  bannerSignature: {
    marginTop: theme.spacing(1),
  },

  //
}));

const VehiclePageView = (props) => {
  const classes = useStyles();
  const [bookingData, setBookingData] = useState({});
  const router = useRouter();
  const {
    t, ssrRender, pageData, reivewsSSRData,
  } = props;
  const {
    images,
    vehicle_details: vehicleDetails,
    special_technical_details: specialDetails,
    technical_details: technicalDetails,
    optional_upsells: optionalUpsells,
    vehicle,
    calendar,
    location,
  } = pageData;
  const esBookingsDisabled = vehicleDetails?.booking_disabled;
  const { mainTitle, breadCrumbsProps, locationHeader } = getVehicleDetails(pageData, t);
  const {
    image, commercial, hasInsurance, joinedOn, name, rate, tripsCount, id, ownerUserId,
  } = getOwnerDetails(pageData);
  const { customizations, guidelines_and_restrictions: guidelinesAndRestrictions } = vehicleDetails;

  const theme = useTheme();
  const isLgWider = useMediaQuery(theme.breakpoints.up('lg'));
  const [snackbar, setSnackbar] = useLayoutSnackbar();
  const { layoutState } = useLayoutState();
  const { handleError } = useHandleResponse();

  const [loading, setLoading] = useLayoutLoadingSpinner();
  const [spinner, setSpinner] = useState('LOADING'); // LOADING, LOADED, NOT_IN_RANGE
  const [errors, setErrors] = useState({});
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);
  const [enableCheckout, setEnableCheckout] = useState(false);
  const [isHourlyMode, setIsHourlyMode] = useState(vehicle.pricing_option === 'hourly');
  const [shouldGetQuote, setShouldGetQuote] = useState(false);
  const [calendarData, setCalendarData] = useState({
    pickupDate: dayjs(router.query.pickup_date).format('YYYY-MM-DD') || '',
    dropoffDate: dayjs(router.query.dropoff_date).format('YYYY-MM-DD') || '',
    pickupTimeSlot: router.query.pickup_time_slot || '',
    dropoffTimeSlot: router.query.dropoff_time_slot || '',
    hourPackage: {},
  });
  const [availabilityData, setAvailabilityData] = useState({
    availablePickupDates: [],
    availableDropoffDates: [],
    availablePickupHours: [],
    availabledropoffHours: [],
    availableHourlyPackages: [],
  });
  const [openAuthOverlay, setOpenAuthOverlay] = useState(false);

  const currentUser = layoutState.current_user;
  const openSummaryDialog = () => {
    setIsSummaryDialogOpen(true);
  };
  const closeSummaryDialog = () => {
    setIsSummaryDialogOpen(false);
  };
  const handleAuthOverlayClose = () => {
    setOpenAuthOverlay(false);
  };

  const handleAuthOverlayOpen = () => {
    setOpenAuthOverlay(true);
  };
  const getQuote = () => {
    Object.keys(errors).length > 0 && setErrors({});
    setSpinner('LOADING');
    setEnableCheckout(false);
    // this is to make sure dropoff is updated with new values whenver user change to hourly mode
    const dropoffTimeSlot = isHourlyMode
      ? dayjs(calendarData.pickupTimeSlot, 'hh:mm a')
        .add(calendarData.hourPackage.number_of_hours, 'h')
        .format('HH:mm')
      : dayjs(calendarData.dropoffTimeSlot, 'h:mm a').format('HH:mm');
    axios
      .get(API.vehicles.bookingDetails.replace('{vehicle_id}', vehicle.id), {
        params: {
          pickup_date: calendarData.pickupDate,
          dropoff_date: isHourlyMode ? calendarData.pickupDate : calendarData.dropoffDate,
          pickup_time_slot: dayjs(calendarData.pickupTimeSlot, 'h:mm a').format('HH:mm'),
          dropoff_time_slot: dropoffTimeSlot,
          hourly_mode: isHourlyMode ? '1' : '0',
        },
      })
      .then((res) => {
        setBookingData(res.data);
        setSpinner('LOADED');
      })
      .catch((error) => {
        handleError(error);
      })
      .then(() => {
        setSpinner('LOADED');
      });
  };
  const vehicleReservations = () => {
    Object.keys(errors).length > 0 && setErrors({});
    setLoading(true);
    const pickupDate = dayjs(calendarData.pickupDate).format('YYYY-MM-DD');
    // Match getQuote: slots from the API/UI are 12-hour (e.g. "12:00 am"). Parsing with
    // 'HH:mm a' yields Invalid Date and breaks hourly checkout ("must be later than pickup").
    let pickupParsed = dayjs(calendarData.pickupTimeSlot, 'h:mm a');
    if (!pickupParsed.isValid()) {
      pickupParsed = dayjs(calendarData.pickupTimeSlot, 'hh:mm a');
    }
    const pickupTime24 = pickupParsed.isValid() ? pickupParsed.format('HH:mm') : '';
    const hourlyHours = Number(calendarData.hourPackage?.number_of_hours);
    const dropoffTime24 = isHourlyMode
      ? (pickupParsed.isValid()
        ? pickupParsed.add(Number.isFinite(hourlyHours) ? hourlyHours : 0, 'hour').format('HH:mm')
        : '')
      : (() => {
        let d = dayjs(calendarData.dropoffTimeSlot, 'h:mm a');
        if (!d.isValid()) d = dayjs(calendarData.dropoffTimeSlot, 'hh:mm a');
        return d.isValid() ? d.format('HH:mm') : '';
      })();
    axios
      .post(API.vehicles.vehicleReservations, {
        pickup_date: pickupDate,
        dropoff_date: isHourlyMode
          ? pickupDate
          : dayjs(calendarData.dropoffDate).format('YYYY-MM-DD'),
        pickup_time_slot: pickupTime24,
        dropoff_time_slot: dropoffTime24,
        vehicle_id: vehicle.id,
        authenticity_token: layoutState.authenticity_token,
        hourly_mode: isHourlyMode ? '1' : '0',
      })
      .then((res) => {
        let query = null;
        if (currentUser && !currentUser.privileged) {
          query = res.data.vehicle_reservation_id;
        } else if (currentUser?.privileged || !currentUser) {
          query = res.data.reservation_identifier;
        }
        Router.push(`/vehicle_reservations/${query}/checkout`);
      })
      .catch((error) => {
        try {
          switch (error.response.status) {
            case 401:
              handleAuthOverlayOpen();
              break;
            case 406:
              setErrors({ ...error.response.data.errors });
              break;
            case 403:
              setSnackbar({
                ...snackbar,
                open: true,
                severity: 'error',
                message: error.response.data.error_message,
              });
              break;
            default:
              handleError(error);
              break;
          }
        } catch (e) {
          handleError(error);
        }
      })
      .then(() => {
        setLoading(false);
      });
  };
  const { formattedMiles, formattedIncrease } = formatMilesLimit(bookingData, t);
  const checkAvailability = () => {
    setSpinner('LOADING');
    setEnableCheckout(false);
    axios
      .get(API.vehicles.availability.replace('{vehicle_id}', vehicle.id), {
        params: {
          pickup_date: calendarData.pickupDate || '',
          dropoff_date: (isHourlyMode ? calendarData.pickupDate : calendarData.dropoffDate) || '',
          pickup_time_slot: calendarData.pickupTimeSlot || '',
          dropoff_time_slot: isHourlyMode ? '' : calendarData.dropoffTimeSlot,
          hourly_mode: isHourlyMode ? '1' : '0',
        },
      })
      .then((res) => {
        setAvailabilityData({
          availablePickupDates: res.data.available_pickup_dates,
          availableDropoffDates: res.data.available_dropoff_dates,
          availablePickupHours: res.data.interval_options.available_pickup_time_slots,
          availableDropoffHours: res.data.interval_options.available_dropoff_time_slots,
          availableHourlyPackages: res.data.interval_options.available_hourly_packages,
        });
        const newCalendar = {
          pickupDate: res.data.interval_options.pickup_date,
          dropoffDate: res.data.interval_options.dropoff_date,
          pickupTimeSlot:
            res.data.interval_options.pickup_time_slot
            || res.data.interval_options.available_pickup_time_slots[0],
          hourPackage: res.data.interval_options.available_hourly_packages[0] || {},
        };
        let dropoffTimeSlot = {};
        if (!isHourlyMode) {
          dropoffTimeSlot = {
            dropoffTimeSlot: res.data.interval_options.available_dropoff_time_slots[0],
          };
        } else if (isHourlyMode && res.data.interval_options.available_hourly_packages[0]) {
          dropoffTimeSlot = {
            dropoffTimeSlot: dayjs(calendarData.pickupTimeSlot, 'hh:mm a')
              .add(res.data.interval_options.available_hourly_packages[0].number_of_hours, 'h')
              .format('hh:mm a'),
          };
        }
        if (JSON.stringify(newCalendar) !== JSON.stringify(calendarData)) {
          setCalendarData((prevData) => ({ ...prevData, ...newCalendar, ...dropoffTimeSlot }));
          // Don't call the booking details endpoint if there is no hourly packages
          if (isHourlyMode) {
            if (res.data.interval_options.available_hourly_packages.length !== 0) {
              setShouldGetQuote(!shouldGetQuote);
            } else {
              setSpinner('LOADED');
              setEnableCheckout(false);
            }
          } else {
            setShouldGetQuote(!shouldGetQuote);
          }
          return;
        }
        setSpinner('LOADED');
      })
      .catch((error) => {
        handleError(error);
      });
  };
  const handleMonthChange = (firstDay, dateType) => new Promise((resolve) => {
    axios
      .get(API.vehicles.availability.replace('{vehicle_id}', vehicle.id), {
        params: {
          month: dayjs(firstDay).format('M'),
          year: dayjs(firstDay).format('YYYY'),
          hourly_mode: isHourlyMode ? '1' : '0',
        },
      })
      .then((res) => {
        if (dateType === 'pickupDate') {
          setAvailabilityData((prevData) => ({
            ...prevData,
            availablePickupDates: res.data.available_pickup_dates,
            availableDropoffDates: res.data.available_dropoff_dates,
            availablePickupHours: res.data.interval_options.available_pickup_time_slots,
            availableDropoffHours: res.data.interval_options.available_dropoff_time_slots,
          }));
          setCalendarData((prevData) => ({
            ...prevData,
            pickupDate: res.data.interval_options.pickup_date,
            dropoffDate: res.data.interval_options.dropoff_date,
            pickupTimeSlot: res.data.interval_options.available_pickup_time_slots[0],
            dropoffTimeSlot: res.data.interval_options.available_dropoff_time_slots[0],
          }));
        } else {
          setAvailabilityData((prevData) => ({
            ...prevData,
            availableDropoffDates: [
              ...new Set([
                ...prevData.availableDropoffDates,
                ...res.data.available_dropoff_dates,
              ]),
            ],
            availableDropoffHours: res.data.interval_options.available_dropoff_time_slots,
          }));
          setCalendarData((prevData) => ({
            ...prevData,
            dropoffDate: res.data.interval_options.dropoff_date,
            dropoffTimeSlot: res.data.interval_options.available_dropoff_time_slots[0],
          }));
        }
        resolve();
      })
      .catch((error) => {
        handleError(error);
      });
  });
  const handleHourlyPackageChange = (value) => {
    let currentHourlyPackage = {};
    availabilityData.availableHourlyPackages.forEach((pack) => {
      if (value === pack.number_of_hours) currentHourlyPackage = pack;
    });
    setCalendarData((prevData) => ({
      ...prevData,
      dropoffTimeSlot: dayjs(calendarData.pickupTimeSlot, 'hh:mm a')
        .add(currentHourlyPackage.number_of_hours, 'h')
        .format('hh:mm a'),
      hourPackage: currentHourlyPackage,
    }));
  };

  useEffect(() => {
    if (ssrRender) return;

    checkAvailability();
  }, [
    calendarData.pickupDate,
    calendarData.dropoffDate,
    calendarData.pickupTimeSlot,
    isHourlyMode,
  ]);
  useEffect(() => {
    if (ssrRender) return;
    if (calendarData.dropoffTimeSlot) {
      getQuote();
    } else {
      setBookingData({});
    }
  }, [shouldGetQuote, calendarData.dropoffTimeSlot]);

  useEffect(() => {
    window.ga(
      'send',
      'event',
      'Rental PDP',
      'Pageview',
      document.getElementsByTagName('h1')[0].innerText,
    );
  }, []);

  const SectionDividerComp = useMemo(() => (
    <Box className={classes.sectionDivider}>
      <Divider />
    </Box>
  ), [classes]);
  return (
    <>

      <Box>
        <StickyBar
          openSummaryDialog={openSummaryDialog}
          bookingData={bookingData}
          price={{ day: vehicle.price }}
          vehicleQuoteId={vehicle.id}
          pricingOption={vehicle.pricing_option}
          hourlyPrice={vehicle.hourly_price}
          showReserveBtn={pageData.calendar && pageData.calendar.working_days.length > 0}
          vehicleDetails={vehicleDetails}
        />

        <Box className={classes.carouselWrapper}>
          <HeroGallery ssrRender={ssrRender} slidesArray={images} />
        </Box>

        <Box className={classes.contentWrapper}>
          <Container>
            {esBookingsDisabled && (
              <Alert severity="warning" className={classes.bookingDisabledBannerAlert}>
                <AlertTitle className={classes.bannerTitle}>
                  {t('fe_es_vehicle_page:booking_disabled_banner.title')}
                </AlertTitle>
                <Box className={classes.bannerMessage}>
                  {t('fe_es_vehicle_page:booking_disabled_banner.message')}
                </Box>
                <Box className={classes.bannerSignature}>
                  {t('fe_es_vehicle_page:booking_disabled_banner.signature')}
                </Box>
              </Alert>
            )}
            <Grid container spacing={4}>
              <Grid item xs={12} lg={8}>
                <Box id="vehicle_description">
                  <VehicleDescription
                    mainTitle={mainTitle}
                    locationHeader={locationHeader}
                    breadCrumbsProps={breadCrumbsProps}
                    vehicleDetails={vehicleDetails}
                    specialDetails={specialDetails}
                    vehicle={vehicle}
                    header={{
                      title: `${pageData.vehicle.make.name} ${pageData.vehicle.model.name} ${pageData.vehicle.year}`,
                      location: locationHeader,
                      trips_count: `${pageData.vehicle.trips_count}`,
                      rate: `${pageData.vehicle.rate}`,
                    }}
                  />
                </Box>

                <Box className={classes.dividerDescription}>
                  <Divider />
                </Box>

                <Box id="vehicle_host">
                  <HostedBy
                    image={image}
                    name={name}
                    id={id}
                    ownerUserId={ownerUserId}
                    rate={rate}
                    trips={tripsCount}
                    date={joinedOn}
                    verified
                    commercialHostToolTip={commercial}
                    translatePath={false}
                  />
                </Box>

                {technicalDetails.length > 0 && (
                  <>
                    {SectionDividerComp}

                    <Box id="vehicle_technical_details">
                      <Box className={classes.technicalDetailsTitle}>
                        <Typography variant="h3" className={classes.sectionTitle}>
                          {t('fe_es_vehicle_page:technical_details')}

                          <span className={classes.sympol}>†</span>
                        </Typography>

                        <Typography variant="body2" className={classes.technicalDetailsHint}>
                          {t('fe_es_vehicle_page:information_shown')}
                        </Typography>
                      </Box>

                      <TechnicalDetails data={technicalDetails} />
                    </Box>
                  </>
                )}

                {SectionDividerComp}

                <MostRidersAskAbout
                  customizations={customizations}
                  optionalUpsells={optionalUpsells}
                  guidelinesAndRestrictions={guidelinesAndRestrictions}
                />
                {!isLgWider && SectionDividerComp}
              </Grid>
              {pageData.calendar && pageData.calendar.working_days.length > 0 && (
                <Grid item xs={12} lg={4}>
                  <VehiclePageQuote
                    bookingData={bookingData}
                    setBookingData={setBookingData}
                    id={vehicle.id}
                    data={calendar}
                    location={location}
                    turnaround={vehicle.turnaround}
                    vehicleDetails={vehicleDetails}
                    price={{ day: vehicle.price }}
                    hasInsurance={hasInsurance}
                    spinner={spinner}
                    vehicleReservations={vehicleReservations}
                    formattedIncrease={formattedIncrease}
                    formattedMiles={formattedMiles}
                    getQuote={getQuote}
                    availabilityData={availabilityData}
                    setAvailabilityData={setAvailabilityData}
                    calendarData={calendarData}
                    setCalendarData={setCalendarData}
                    checkAvailability={checkAvailability}
                    handleMonthChange={handleMonthChange}
                    pricingOption={vehicle.pricing_option}
                    hourlyPrice={vehicle.hourly_price}
                    handleHourlyPackageChange={handleHourlyPackageChange}
                    isHourlyMode={isHourlyMode}
                    setIsHourlyMode={setIsHourlyMode}
                    errors={errors}
                    setEnableCheckout={setEnableCheckout}
                    enableCheckout={enableCheckout}
                  />
                </Grid>
              )}
            </Grid>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={8}>
                <Box id="vehicle_reviews" mb={3}>
                  <Reviews rate={vehicle.rate} reivewsSSRData={reivewsSSRData} isLgWider={isLgWider} SectionDividerComp={SectionDividerComp} />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {isSummaryDialogOpen && (
          <SummaryDialog
            closeHandler={closeSummaryDialog}
            isOpen={isSummaryDialogOpen}
            bookingData={bookingData}
            price={{ day: vehicle.price }}
            location={location}
            setBookingData={setBookingData}
            id={vehicle.id}
            data={calendar}
            turnaround={vehicle.turnaround}
            vehicleDetails={vehicleDetails}
            hasInsurance={hasInsurance}
            spinner={spinner}
            vehicleReservations={vehicleReservations}
            formattedIncrease={formattedIncrease}
            formattedMiles={formattedMiles}
            errors={errors}
            getQuote={getQuote}
            availabilityData={availabilityData}
            setAvailabilityData={setAvailabilityData}
            calendarData={calendarData}
            setCalendarData={setCalendarData}
            checkAvailability={checkAvailability}
            handleMonthChange={handleMonthChange}
            handleHourlyPackageChange={handleHourlyPackageChange}
            isHourlyMode={isHourlyMode}
            pricingOption={vehicle.pricing_option}
            setIsHourlyMode={setIsHourlyMode}
            setEnableCheckout={setEnableCheckout}
            enableCheckout={enableCheckout}
            hourlyPrice={vehicle.hourly_price}
          />
        )}

        {openAuthOverlay && (
          <AuthOverlay
            openAuthOverlay={openAuthOverlay}
            handleAuthOverlayClose={handleAuthOverlayClose}
            handleAuthOverlayOpen={handleAuthOverlayOpen}
          />
        )}
      </Box>
    </>
  );
};

export default withTranslation(['fe_es_vehicle_page', 'fe_bike_owner_widget'])(VehiclePageView);
